// src/components/ScriptIntelligenceTool.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScriptIntelligence.css';
import { event } from '../utils/gtm';
import UpgradeError from './UpgradeError';
import InfoTooltip from './InfoTooltip';

// This function takes the flat history array and groups it by date categories.
const groupHistoryByDate = (history) => {
  const groups = {
    Today: [],
    Yesterday: [],
    'Last 7 Days': [],
    'Older': []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);

  history.forEach(item => {
    const itemDate = new Date(item.created_at);
    if (itemDate >= today) {
      groups.Today.push(item);
    } else if (itemDate >= yesterday) {
      groups.Yesterday.push(item);
    } else if (itemDate >= last7Days) {
      groups['Last 7 Days'].push(item);
    } else {
      groups.Older.push(item);
    }
  });

  return groups;
};

const ScriptIntelligenceTool = ({
  user,
  script,
  setScript,
  analysis,
  isLoading,
  error,
  isRegeneratingTitles,
  handleAnalyze,
  handleRegenerateTitles,
  history,
  deleteAnalysis,
  loadAnalysisFromHistory,
  actionCosts,
  triggerFeedbackModal
}) => {
  const navigate = useNavigate();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // A small, reusable component for displaying locked feature sections
  const LockedSection = ({ title, text, cta }) => {
    return (
      <div className="result-section locked-section-wrapper">
        <h3>{title} <span className="pro-badge">PRO</span></h3>
        <div className="locked-content-overlay">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="locked-icon"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
          <h4>{cta}</h4>
          <p>{text}</p>
          <button onClick={() => {
              event({ action: 'click_upgrade_intent', category: 'Monetization', label: `Source: Script Intelligence ${title.replace(/\s/g, '')}` });
              navigate('/pricing');
            }} className="rounded-full bg-gradient-to-r from-[#1d94d0] to-[#1E3A8A] px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#1d94d0]/40 hover:brightness-110">Upgrade to Pro</button>
        </div>
      </div>
    );
  };

  const copyToClipboard = (text, clickEvent, label) => {
    navigator.clipboard.writeText(text);
    event({ action: 'copy_text', category: 'Engagement', label: `Script Intelligence: ${label}` });
    const element = clickEvent.currentTarget;
    if (element) {
      const isPill = element.classList.contains('tag-pill');
      const originalContent = element.innerHTML;

      // For buttons, show a checkmark. For pills, just change color.
      if (!isPill) {
        const checkIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`;
        element.innerHTML = checkIcon;
      }
      element.classList.add('copied');

      setTimeout(() => {
        try {
          if (!isPill) {
            element.innerHTML = originalContent;
          }
          element.classList.remove('copied');
        } catch (e) { /* Ignore */ }
      }, 1500);
    }
  };

  // A safe function to render simple markdown (bold) as HTML
  const renderFormattedText = (text) => {
    if (!text) return null;
    // Replace **text** with <strong>text</strong>
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return (
      <p style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: formattedText }} />
    );
  };

  const groupedHistory = groupHistoryByDate(history);

  return (
    <div className="tool-container script-intelligence-container">
      <header className="tool-header">
        <h1>Script Intelligence</h1>
        <p>Optimize your video before you film. Get AI-powered titles, descriptions, and retention analysis.</p>
      </header>
      
      <div className="script-input-card">
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder="Paste your full video script here..."
          className="script-textarea"
        />
        <button onClick={handleAnalyze} disabled={isLoading} className="generate-titles-button analyze-button">
          {isLoading ? (
            <>
              <div className="button-spinner-light"></div>
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.572L16.5 21.75l-.398-1.178a3.375 3.375 0 00-2.455-2.456L12.75 18l1.178-.398a3.375 3.375 0 002.455-2.456L16.5 14.25l.398 1.178a3.375 3.375 0 002.456 2.456L20.25 18l-1.178.398a3.375 3.375 0 00-2.456 2.456z" /></svg>
              <span>Analyze Script</span>
              <div className="cost-badge">
                <span>{actionCosts.analyze_script}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins"><defs><linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fef08a"/><stop offset="100%" stopColor="#facc15"/></linearGradient><linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eab308"/><stop offset="100%" stopColor="#ca8a04"/></linearGradient></defs><ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/></svg>
              </div>
            </>
          )}
        </button>
        {error && (
          error.toLowerCase().includes("credits") ?
          <UpgradeError message={error} source="Script Intelligence" /> :
          <p className="error-message" style={{textAlign: 'center', marginTop: '15px'}}>{error}</p> // Keep for non-credit errors
        )}
      </div>
      
      <div className="history-panel">
        <button className="accordion-toggle" onClick={() => setIsHistoryOpen(!isHistoryOpen)}>
          <h3>Analysis History</h3>
          <span className={`accordion-arrow ${isHistoryOpen ? 'is-open' : ''}`}>›</span>
        </button>
        <div className={`history-list-collapsible ${isHistoryOpen ? 'is-open' : ''}`}>
          <ul className="history-list">
            {history && history.length > 0 ? (
              Object.keys(groupedHistory).map(groupName => (
                groupedHistory[groupName].length > 0 && (
                  <div key={groupName} className="history-group">
                    <h4 className="history-group-header">{groupName}</h4>
                    <ul>
                      {groupedHistory[groupName].map(item => (
                        <li key={item.id} onClick={() => loadAnalysisFromHistory(item)}>
                          <span className="history-preview">{item.script_preview}</span>
                          <button 
                            className="delete-history-btn"
                            onClick={(e) => { e.stopPropagation(); deleteAnalysis(item.id); }}
                          >&times;</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))
            ) : (
              <p className="no-history-message">No analyses saved yet.</p>
            )}
          </ul>
        </div>
      </div>

      {isLoading && <div className="loading-spinner"></div>}
      
      {analysis && !isLoading && (
        <div className="script-results-container">
          <div className="result-section">
            <h3>
              Target Audience
              <InfoTooltip content="Based on your script, this is the ideal viewer profile you should be targeting. Use this to guide your video's tone and style." />
            </h3>
            <p className="audience-persona">{analysis.audience_persona}</p>
          </div>

          {analysis.content_angle && (
            <div className="result-section">
              <h3>
                Content Angle
                <InfoTooltip content="This is the unique 'big promise' of your video. It's what makes it stand out from other videos on the same topic and is key for branding." />
              </h3>
              <p className="audience-persona">{analysis.content_angle}</p>
            </div>
          )}
          <div className="result-section">
            <h3>
              Main Keywords
              <InfoTooltip content="These are the most important keywords and topics identified in your script. Ensure these are present in your title, description, and tags." />
              </h3>
            <div className="tags-container">{analysis.main_keywords.map(kw => <span key={kw} className="tag-pill" onClick={(e) => copyToClipboard(kw, e, 'Keyword')} title="Click to copy">{kw}</span>)}</div>
          </div>
          <div className="result-section">
            <div className="section-header-with-action">
              <h3>Title Ideas<InfoTooltip content="AI-generated title suggestions designed to be catchy and SEO-friendly. Click Regenerate to get new ideas." /></h3>
            </div>
            {analysis.title_ideas && analysis.title_ideas.length > 0 && (
              <div className="regenerate-button-container">
                <button onClick={handleRegenerateTitles} disabled={isRegeneratingTitles} className="regenerate-button generate-titles-button">
                  {isRegeneratingTitles ? (
                    <>
                      <div className="button-spinner-light"></div>
                      <span>Regenerating...</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.572L16.5 21.75l-.398-1.178a3.375 3.375 0 00-2.455-2.456L12.75 18l1.178-.398a3.375 3.375 0 002.455-2.456L16.5 14.25l.398 1.178a3.375 3.375 0 002.456 2.456L20.25 18l-1.178.398a3.375 3.375 0 00-2.456 2.456z" />
                      </svg>
                      <span>Regenerate</span>
                      <div className="cost-badge">
                        <span>{actionCosts.regenerate_script_titles}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins"><defs><linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fef08a"/><stop offset="100%" stopColor="#facc15"/></linearGradient><linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eab308"/><stop offset="100%" stopColor="#ca8a04"/></linearGradient></defs><ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/></svg>
                      </div>
                    </>
                  )}
                </button>
              </div>
            )}
            <ul className="title-ideas-list">
              {/* Show all titles for Pro, or just the first for Free */}
              {(user.plan === 'pro' ? analysis.title_ideas : analysis.title_ideas.slice(0, 1)).map((title, i) => (
                <li key={i} className="title-idea-item">
                  <span>{title}</span>
                  <button className="copy-title-btn" onClick={(e) => copyToClipboard(title, e, 'Title Idea')} title="Copy">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" /></svg>
                  </button>
                </li>
              ))}

              {/* For Free users, show a locked card for the rest */}
              {user.plan !== 'pro' && analysis.title_ideas.length > 1 && (
                <li className="title-idea-item locked-item-wrapper">
                  <div className="locked-content-overlay">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="locked-icon"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                    <h4>Unlock {analysis.title_ideas.length - 1} More Title Ideas</h4>
                    <button onClick={() => {
                        event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Script Intelligence Titles' });
                        navigate('/pricing');
                      }} className="rounded-full bg-gradient-to-r from-[#1d94d0] to-[#1E3A8A] px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#1d94d0]/40 hover:brightness-110">Upgrade to Pro</button>
                  </div>
                </li>
              )}
            </ul>
          </div>

          {user.plan === 'pro' ? (
            <>
              {analysis.hook_analysis && (
                <div className="result-section">
                  <h3>
                    Hook Analysis (First 15s)
                    <InfoTooltip content="A strong hook is critical for viewer retention. This analysis critiques the start of your script and offers suggestions to grab viewers immediately." />
                  </h3>
                  <div className="description-box">
                    {renderFormattedText(analysis.hook_analysis)}
                  </div>
                </div>
              )}
              {analysis.retention_report && (
                <div className="result-section">
                  <h3>
                    Retention Report
                    <InfoTooltip content="Identifies potential 'drop-off' points in your script where viewers might get bored. Use the suggested 'pattern interrupts' to keep them engaged." />
                  </h3>
                  <div className="description-box">
                    {renderFormattedText(analysis.retention_report)}
                  </div>
                </div>
              )}
              <div className="result-section">
                <h3>
                  Description Options
                  <InfoTooltip content="Ready-to-use descriptions generated from your script. Pick one and customize it with your links and calls-to-action." />
                </h3>
                {analysis.description_options.map((desc, i) => (
                  <div key={i} className="description-box">
                    <p>{desc}</p>
                    <button className="copy-title-btn" onClick={(e) => copyToClipboard(desc, e, 'Description')} title="Copy">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" /></svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="result-section">
                <h3>
                  Tags & Hashtags
                  <InfoTooltip content="A mix of broad and specific tags to help YouTube's algorithm understand and categorize your video. Copy and paste these directly into your video's tag section." />
                </h3>
                <div className="tags-container">
                  {analysis.tags_and_hashtags.map(tag => <span key={tag} className="tag-pill" onClick={(e) => copyToClipboard(tag, e, 'Tag')} title="Click to copy">{tag}</span>)}
                </div>
              </div>
              <div className="result-section">
                <h3>
                  Thumbnail Concepts
                  <InfoTooltip content="Creative ideas for your video's thumbnail. Use these as inspiration to create a high-click-through-rate thumbnail that grabs attention." />
                </h3>
                <div className="thumbnail-ideas-container">
                  {analysis.thumbnail_ideas.map((idea, i) => (
                    <div key={i} className="thumbnail-card">
                      <p>{idea}</p>
                      <button className="copy-title-btn" onClick={(e) => copyToClipboard(idea, e, 'Thumbnail Concept')} title="Copy">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <LockedSection 
                title="Hook Analysis" 
                cta="Unlock Your Script's Hook"
                text="Get an expert analysis of your video's first 15 seconds to maximize viewer retention."
              />
              <LockedSection 
                title="Retention Report" 
                cta="Find & Fix Boring Parts"
                text="Discover where viewers might drop off and get actionable advice to keep them watching."
              />
              <LockedSection 
                title="Description Options" 
                cta="Unlock AI Descriptions"
                text="Get multiple, ready-to-use description options optimized for your script."
              />
              <LockedSection 
                title="Tags & Hashtags" 
                cta="Unlock Tags & Hashtags"
                text="Instantly generate a full list of relevant tags and hashtags to maximize reach."
              />
              <LockedSection 
                title="Thumbnail Concepts" 
                cta="Unlock Thumbnail Ideas"
                text="Receive creative, data-driven concepts for your video's thumbnail."
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptIntelligenceTool;